import { useEffect, useId, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2 } from 'lucide-react';
import { NETWORK_LIFECYCLE_LABELS_UK, normalizeNetworkLifecycle, type NetworkLifecycle } from '@ukrop-nm/shared';
import { diagramStore } from '../state/diagramStore';
import { isBuiltinSite, listCustomSites, getSiteDisplayTitle, type CustomSiteRecord } from '../state/siteIndex';
import { UkropTip } from '../tooltips/DiagramTooltipContext';
import { ACCOUNT_CHANGED_EVENT } from '../auth/accountScope';
import { useAuth } from '../auth/AuthContext';

type SiteCard = {
  id: string;
  title: string;
  desc: string;
  badge: string;
  badgeClass: string;
  builtin: boolean;
};

const BUILTIN_SITES: SiteCard[] = [
  {
    id: 'SITE-OFFICE-3F',
    title: 'Триповерховий офіс',
    desc: 'Еталонна схема: повне обладнання, VLAN і підмережі. Шаблон не видаляється.',
    badge: 'Еталон',
    badgeClass: 'bg-brand-50 text-brand-600',
    builtin: true,
  },
];

const LIFECYCLE_BADGE: Record<NetworkLifecycle, string> = {
  draft: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-200',
  deployment: 'bg-warning-50 text-warning-600',
  modernization: 'bg-brand-50 text-brand-600',
  reconfiguration: 'bg-brand-50 text-brand-700',
  testing: 'bg-orange-50 text-orange-600',
  production: 'bg-success-50 text-success-600',
  maintenance: 'bg-orange-50 text-orange-700',
  archive: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400',
};

function customToCard(site: CustomSiteRecord): SiteCard {
  const lifecycle = normalizeNetworkLifecycle(site.lifecycle);
  return {
    id: site.id,
    title: getSiteDisplayTitle(site),
    desc: 'Проєкт схеми на сервері. Після впровадження можна підключити скрипт моніторингу в цьому ж обʼєкті.',
    badge: NETWORK_LIFECYCLE_LABELS_UK[lifecycle],
    badgeClass: LIFECYCLE_BADGE[lifecycle],
    builtin: false,
  };
}

/** ~44pt control height (Apple HIG). Compact horizontal padding so 4 controls fit one row. */
const BTN =
  'inline-flex h-11 w-full min-h-11 items-center justify-center rounded-xl px-2 text-sm font-medium whitespace-nowrap transition';

const FIELD =
  'focus-ring h-11 w-full rounded-xl border border-gray-200 bg-white px-3 text-sm text-gray-800 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-200';

export function SitesPage() {
  const navigate = useNavigate();
  const { syncState } = useAuth();
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [customSites, setCustomSites] = useState(listCustomSites);
  const [newTitle, setNewTitle] = useState('');
  const cancelRef = useRef<HTMLButtonElement>(null);
  const titleId = useId();
  const descId = useId();

  useEffect(() => {
    const refresh = () => setCustomSites(listCustomSites());
    window.addEventListener(ACCOUNT_CHANGED_EVENT, refresh);
    return () => window.removeEventListener(ACCOUNT_CHANGED_EVENT, refresh);
  }, []);

  useEffect(() => {
    if (pendingDeleteId) cancelRef.current?.focus();
  }, [pendingDeleteId]);

  const sites = [...BUILTIN_SITES, ...customSites.map(customToCard)];

  const confirmDelete = () => {
    if (!pendingDeleteId || isBuiltinSite(pendingDeleteId)) {
      setPendingDeleteId(null);
      return;
    }
    const id = pendingDeleteId;
    diagramStore.clearSiteLocalData(id);
    setCustomSites(listCustomSites());
    setPendingDeleteId(null);
    if (window.location.pathname.includes(`/site/${id}`)) {
      void navigate('/');
    }
  };

  const createSite = () => {
    const id = diagramStore.createPlannedSite(newTitle);
    setNewTitle('');
    setCustomSites(listCustomSites());
    void navigate(`/site/${id}/edit`);
  };

  return (
    <div className="p-4 md:p-6">
      <header className="mb-6">
        <h2 className="page-title">Обʼєкти моніторингу</h2>
        <p className="page-desc max-w-3xl">
          Єдина вбудована схема — еталон триповерхового офісу. Редагування йде в браузері.
          У хмару схема записується лише кнопкою «Зберегти» або при виході з акаунта.
          {syncState === 'saving'
            ? ' Збереження на сервер…'
            : syncState === 'error'
              ? ' Не вдалося зберегти на сервер — натисніть «Хмара: помилка», щоб повторити.'
              : syncState === 'dirty'
                ? ' Є локальні зміни, яких ще немає в хмарі.'
              : syncState === 'saved'
                ? ' Остання збережена копія в хмарі.'
                : ''}
        </p>
      </header>

      <div className="grid items-stretch gap-4 lg:grid-cols-2">
        <article className="card flex h-full flex-col gap-4 border-dashed">
          <h3 className="text-base font-semibold tracking-tight text-gray-900 dark:text-gray-100">
            Новий обʼєкт
          </h3>
          <p className="text-sm leading-5 text-gray-600 dark:text-gray-300">
            Порожня схема зі статусом «Проектування (Чернетка)». Поки не натиснете «Зберегти»
            або не вийдете з акаунта, обʼєкт лишається лише в цьому браузері.
          </p>
          <form
            className="mt-auto grid gap-2 border-t border-gray-100 pt-4 dark:border-gray-800 sm:grid-cols-[1fr_auto]"
            onSubmit={(e) => {
              e.preventDefault();
              createSite();
            }}
          >
            <input
              className={FIELD}
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              placeholder="Назва обʼєкта / проєкту"
              aria-label="Назва нового обʼєкта"
            />
            <button
              type="submit"
              className={`${BTN} w-auto min-w-40 bg-brand-500 px-4 text-white shadow-theme-sm hover:bg-brand-600`}
            >
              Створити схему
            </button>
          </form>
        </article>

        {sites.map((site) => (
          <article key={site.id} className="card flex h-full flex-col gap-4">
            <div className="flex items-center justify-between gap-3">
              <h3 className="min-w-0 truncate text-base font-semibold tracking-tight text-gray-900">
                {site.title}
              </h3>
              <span
                className={`shrink-0 rounded-full px-2.5 py-1 text-theme-xs font-medium whitespace-nowrap ${site.badgeClass}`}
              >
                {site.badge}
              </span>
            </div>

            <p className="text-sm leading-5 text-gray-600 dark:text-gray-300">
              {site.builtin ? site.desc : `${site.id} · ${site.desc}`}
            </p>

            <div
              className={`mt-auto grid items-center gap-2 border-t border-gray-100 pt-4 dark:border-gray-800 ${
                site.builtin
                  ? 'grid-cols-3'
                  : 'grid-cols-[1fr_1fr_1fr_2.75rem]'
              }`}
              role="toolbar"
              aria-label={`Дії для ${site.id}`}
            >
              <Link
                to={`/site/${site.id}`}
                className={`${BTN} bg-brand-500 text-white shadow-theme-sm hover:bg-brand-600`}
              >
                Моніторинг
              </Link>
              <Link
                to={`/site/${site.id}/edit`}
                className={`${BTN} border border-gray-200 bg-white text-gray-800 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-200 dark:hover:bg-gray-800`}
              >
                Редактор
              </Link>
              <Link
                to={`/site/${site.id}/devices`}
                className={`${BTN} border border-gray-200 bg-white text-gray-800 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-200 dark:hover:bg-gray-800`}
              >
                Обладнання
              </Link>
              {site.builtin ? null : (
                <UkropTip text="Видалити проєкт">
                  <button
                    type="button"
                    className={`${BTN} text-error-500 hover:bg-error-50 active:bg-error-50 dark:hover:bg-error-500/15`}
                    aria-label={`Видалити локальні дані ${site.id}`}
                    onClick={() => setPendingDeleteId(site.id)}
                  >
                    <Trash2 size={18} strokeWidth={1.75} aria-hidden />
                  </button>
                </UkropTip>
              )}
            </div>
          </article>
        ))}
      </div>

      {pendingDeleteId ? (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-gray-900/40 p-4"
          role="presentation"
          onClick={() => setPendingDeleteId(null)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setPendingDeleteId(null);
          }}
        >
          <div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby={titleId}
            aria-describedby={descId}
            className="w-full max-w-[400px] rounded-2xl bg-white p-6 shadow-theme-lg dark:border dark:border-gray-700 dark:bg-gray-900"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id={titleId} className="text-center text-lg font-semibold text-gray-900 dark:text-gray-100">
              Видалити локальні дані?
            </h3>
            <p id={descId} className="mt-2 text-center text-sm leading-5 text-gray-600 dark:text-gray-300">
              Проєкт і схему{' '}
              <span className="font-medium text-gray-800 dark:text-gray-200">{pendingDeleteId}</span>{' '}
              буде видалено з цього браузера. Показові шаблони залишаться. Цю дію не можна скасувати.
            </p>
            <div className="mt-6 grid grid-cols-2 gap-3">
              <button
                ref={cancelRef}
                type="button"
                className={`${BTN} border border-gray-200 bg-white px-4 text-gray-800 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-200 dark:hover:bg-gray-800`}
                onClick={() => setPendingDeleteId(null)}
              >
                Скасувати
              </button>
              <button
                type="button"
                className={`${BTN} bg-error-500 px-4 text-white hover:bg-error-500/90`}
                onClick={confirmDelete}
              >
                Видалити
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
